import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Button, TextField, Snackbar, Alert } from '@mui/material';
import { IWarehouseDTO, IUpdateWarehouseDTO } from '../../interfaces/Organization';
import { updateWarehouse } from '../../services/Organization';
import { useAuth } from '../../auth/AuthProvider';

interface EditOrganizationProps {
  warehouseData: IWarehouseDTO | null;
  onCancel: () => void;
  onSave: () => void;
}

const EditOrganization = ({ warehouseData, onCancel, onSave }: EditOrganizationProps) => {
  const [warehouseName, setWarehouseName] = useState('');
  const [warehouseAddress, setWarehouseAddress] = useState('');
  const [warehouseLat, setWarehouseLat] = useState(0);
  const [warehouseLng, setWarehouseLng] = useState(0);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [insideImageFile, setInsideImageFile] = useState<File | null>(null);
  const [outsideImageFile, setOutsideImageFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [insidePreview, setInsidePreview] = useState<string | null>(null);
  const [outsidePreview, setOutsidePreview] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const WAREHOUSE_ID = loggedInUser?.workspaceId;

  useEffect(() => {
    if (warehouseData) {
      setWarehouseName(warehouseData.name || '');
      setWarehouseAddress(warehouseData.address || '');
      setWarehouseLat(warehouseData.latitude || 0);
      setWarehouseLng(warehouseData.longitude || 0);
      
      // Set existing image previews
      if (warehouseData.logo) setLogoPreview(warehouseData.logo);
      if (warehouseData.insideImgUrl) setInsidePreview(warehouseData.insideImgUrl);
      if (warehouseData.outsideImgUrl) setOutsidePreview(warehouseData.outsideImgUrl);
    }
  }, [warehouseData]);

  useEffect(() => {
    if (!addressInputRef.current || !window.google?.maps?.places) return;
    const ac = new google.maps.places.Autocomplete(addressInputRef.current, { types: ["geocode", "establishment"] });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (place?.geometry?.location) {
        setWarehouseAddress(place.formatted_address || place.name || '');
        setWarehouseLat(place.geometry.location.lat());
        setWarehouseLng(place.geometry.location.lng());
        setHasChanges(true);
      }
    });
    autocompleteRef.current = ac;
  }, [addressInputRef.current]);

  const handleFileUpload = (type: 'logo' | 'inside' | 'outside') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setSnackbarMessage('Only JPG, JPEG, and PNG files are allowed');
      setShowSnackbar(true);
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setSnackbarMessage('File size must be less than 5MB');
      setShowSnackbar(true);
      return;
    }

    const preview = URL.createObjectURL(file);
    
    switch (type) {
      case 'logo':
        setLogoFile(file);
        setLogoPreview(preview);
        break;
      case 'inside':
        setInsideImageFile(file);
        setInsidePreview(preview);
        break;
      case 'outside':
        setOutsideImageFile(file);
        setOutsidePreview(preview);
        break;
    }
    setHasChanges(true);
  };

  const handleRemoveFile = (type: 'logo' | 'inside' | 'outside') => {
    switch (type) {
      case 'logo':
        setLogoFile(null);
        setLogoPreview(warehouseData?.logo || null);
        break;
      case 'inside':
        setInsideImageFile(null);
        setInsidePreview(warehouseData?.insideImgUrl || null);
        break;
      case 'outside':
        setOutsideImageFile(null);
        setOutsidePreview(warehouseData?.outsideImgUrl || null);
        break;
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!WAREHOUSE_ID) {
      setSnackbarMessage('Warehouse ID is not available');
      setShowSnackbar(true);
      return;
    }
    
    // Check if there are any changes
    const nameChanged = warehouseName !== (warehouseData?.name || '');
    const addressChanged = warehouseAddress !== (warehouseData?.address || '');
    const hasFileChanges = logoFile || insideImageFile || outsideImageFile;
    
    if (!nameChanged && !addressChanged && !hasFileChanges) {
      setSnackbarMessage('No changes to save');
      setShowSnackbar(true);
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      
      // Create DTO matching backend UpdateWarehouseDTO
      const dto: IUpdateWarehouseDTO = {
        name: warehouseName,
        address: warehouseAddress,
        latitude: warehouseLat,
        longitude: warehouseLng,
      };
      
      // Add DTO as JSON blob (backend expects @RequestPart("data"))
      formData.append('data', new Blob([JSON.stringify(dto)], { type: "application/json" }));
      
      // Add image files only if they exist
      if (logoFile) {
        formData.append('logo', logoFile);
      }
      if (insideImageFile) {
        formData.append('insideImage', insideImageFile);
      }
      if (outsideImageFile) {
        formData.append('outsideImage', outsideImageFile);
      }
      
      const response = await updateWarehouse(WAREHOUSE_ID, formData);
      
      // Handle response based on the actual API response structure
      if (response && (response.status === 'success' || response.success)) {
        setSnackbarMessage(response.message || 'Organization updated successfully');
        setShowSnackbar(true);
        setHasChanges(false);
        
        // Redirect to organization page after showing success message
        setTimeout(() => {
          onSave();
        }, 2000);
      } else {
        setSnackbarMessage(response?.message || 'Failed to update organization');
        setShowSnackbar(true);
      }
      
    } catch (error: any) {
      console.error('Error updating organization:', error);
      
      // Handle different types of errors
      let errorMessage = 'Failed to update organization';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSnackbarMessage(errorMessage);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 3.5rem)', p: 1, display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ 
        borderRadius: 2, 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          p: 1,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}>
          <Box sx={{ mb:2}}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>Warehouse Name</Typography>
            <Typography variant="caption" color="grey.600" sx={{ mb:1, display: 'block' }}>Edit a Name for your Warehouse</Typography>
            <TextField
              value={warehouseName}
              onChange={(e) => {
                setWarehouseName(e.target.value);
                setHasChanges(true);
              }}
              size="small" fullWidth
              sx={{ maxWidth:450, bgcolor: 'grey.50' }}
            />
          </Box>

          <Box sx={{ mb:2}}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>Warehouse Address</Typography>
            <Typography variant="caption" color="grey.600" sx={{ mb:1, display: 'block' }}>Edit the Address for your Warehouse</Typography>
            <TextField
              value={warehouseAddress}
              onChange={(e) => {
                setWarehouseAddress(e.target.value);
                setHasChanges(true);
              }}
              inputRef={addressInputRef}
              size="small"
              fullWidth
              sx={{ maxWidth:450, bgcolor: 'grey.50' }}
            />
          </Box>

          <Box sx={{ mb:2}}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>Organization Logo</Typography>
            <Typography variant="caption" color="grey.600" sx={{ mb:1, display: 'block' }}>Assign a Logo for your organization</Typography>
            
            <Box sx={{ width: 120, height: 120, borderRadius: '50%', border: 2, borderColor: 'grey.200', overflow: 'hidden', mb: 2 }}>
              <Box component="img" 
                src={logoPreview || "/sidebar_Logo.png"} 
                alt="Logo" 
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }} 
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Note</Typography>
              <Typography variant="caption" color="grey.600" component="div">
                • JPG, JPEG, and PNG are allowed<br />
                • Maximum upload file size ~ 1MB
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button 
                variant="outlined" 
                component="label"
                sx={{
                  borderRadius: "1.75rem",
                  border: "1px solid",
                  borderColor: "grey.100",
                  color: "primary.main",
                  textTransform: "none",
                  px: "1rem",
                  py: "0.125rem",
                  "&:hover": { borderColor: "grey.100" }
                }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleFileUpload('logo')}
                />
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => handleRemoveFile('logo')}
                sx={{
                  borderRadius: "1.75rem",
                  border: "1px solid",
                  borderColor: "error.light",
                  color: "error.main",
                  textTransform: "none",
                  px: "1rem",
                  py: "0.125rem",
                  "&:hover": { borderColor: "error.light" }
                }}
              >
                Remove
              </Button>
            </Box>
          </Box>
          <Box sx={{ mb:2}}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>Warehouse Images</Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="grey.600" fontWeight={600} sx={{ mb: 1 }}>Inside Image</Typography>
                <Box sx={{ width: 150, height: 150, borderRadius: '50%', border: 2, borderColor: 'grey.200', overflow: 'hidden', mb: 2 }}>
                  <Box component="img" 
                    src={insidePreview || "/sidebar_Logo.png"} 
                    alt="Inside" 
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button 
                    variant="outlined" 
                    size='small'
                    component="label"
                    sx={{
                      borderRadius: "1.75rem",
                      border: "1px solid",
                      borderColor: "grey.100",
                      color: "primary.main",
                      textTransform: "none",
                      px: "1rem",
                      py: "0.125rem",
                      "&:hover": { borderColor: "grey.100" }
                    }}
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileUpload('inside')}
                    />
                  </Button>
                  <Button 
                    variant="outlined" 
                    size='small'
                    onClick={() => handleRemoveFile('inside')}
                    sx={{
                      borderRadius: "1.75rem",
                      border: "1px solid",
                      borderColor: "error.light",
                      color: "error.main",
                      textTransform: "none",
                      px: "1rem",
                      py: "0.125rem",
                      "&:hover": { borderColor: "error.light" }
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="grey.600" fontWeight={600} sx={{ mb: 1 }}>Outside Image</Typography>
                <Box sx={{ width: 150, height: 150, borderRadius: '50%', border: 2, borderColor: 'grey.200', overflow: 'hidden', mb: 2 }}>
                  <Box component="img" 
                    src={outsidePreview || "/sidebar_Logo.png"} 
                    alt="Outside" 
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button 
                    size='small'
                    variant="outlined" 
                    component="label"
                    sx={{
                      borderRadius: "1.75rem",
                      border: "1px solid",
                      borderColor: "grey.100",
                      color: "primary.main",
                      textTransform: "none",
                      px: "1rem",
                      py: "0.125rem",
                      "&:hover": { borderColor: "grey.100" }
                    }}
                  >
                    Upload
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileUpload('outside')}
                    />
                  </Button>
                  <Button 
                    size='small'
                    variant="outlined" 
                    onClick={() => handleRemoveFile('outside')}
                    sx={{
                      borderRadius: "1.75rem",
                      border: "1px solid",
                      borderColor: "error.light",
                      color: "error.main",
                      textTransform: "none",
                      px: "1rem",
                      py: "0.125rem",
                      "&:hover": { borderColor: "error.light" }
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, pt: 2, borderTop: 1, borderColor: 'grey.200' }}>
          <Button 
            variant="outlined" 
            onClick={onCancel}
            sx={{
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
              padding: "8px 16px",
              textTransform: "none"
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={loading}
            sx={{
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
              padding: "8px 40px",
              textTransform: "none",
              "&:hover": {
                bgcolor: "primary.dark"
              }
            }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Paper>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarMessage.includes('successfully') ? 'success' : 'error'}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: snackbarMessage.includes('successfully') ? " success.main" : "error.main",
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditOrganization;
